import { useProposalForm } from '@/features/proposals/hooks/use-proposal-form.hook';
import { cleanCnpjOrCpf } from '@/shared/utils/document.util';
import { cnpjService } from '@/services/cnpj';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { useFetchZipcode } from '@/shared/hooks/use-fetch-zipcode.hook';
import { toaster } from '@/shared/ui/toaster.component';
import { validatePhone } from '@/shared/utils';
import { Text } from '@noorden-insurtech/alba-design-system';
import { DateTime } from 'luxon';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PME_ACTIVITY_OPTIONS, PME_UF_OPTIONS } from '../../proposal.mock';
import {
  type CepAddressField,
  type CepLockedFields,
  resolveCepAddressFill,
} from '../../utils/cep-address-fill.util';
import type { PmeStipulantValues } from '../../utils/hydrate-proposal.util';

const pmeStipulantDefaultValues: PmeStipulantValues = {
  cnpj: '',
  isCaepf: false,
  companyName: '',
  mainActivity: '',
  gender: '',
  dateOfBirth: '',
  email: '',
  phone: '',
  cep: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
};

/**
 * Validador de campo obrigatório do endereço — só ligado com
 * `requireAddressFields` (proposta). O endereço completo vai para o i4Pro e para
 * os documentos da assinatura; a Api cobra os mesmos campos ao salvar a etapa.
 */
const requiredWhen = (enabled: boolean, message: string) =>
  enabled
    ? {
        onChange: ({ value }: { value: unknown }) =>
          String(value ?? '').trim() ? undefined : message,
      }
    : undefined;

/** CEP completo: 8 dígitos. */
const cepValidator = (enabled: boolean) =>
  enabled
    ? {
        onChange: ({ value }: { value: unknown }) => {
          const digits = String(value ?? '').replace(/\D/g, '');
          if (!digits) return 'Informe o CEP';
          return digits.length === 8 ? undefined : 'CEP inválido';
        },
      }
    : undefined;

const onlyDigits = (raw: unknown) => String(raw ?? '').replace(/\D/g, '');

const PME_GENDER_OPTIONS = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Feminino' },
];

export type PmeStipulantFormSectionHandle = {
  validateRequired: () => Promise<boolean>;
  getValues: () => PmeStipulantValues;
};

type Props = {
  showContact?: boolean;
  /** Valores hidratados da cotação (estipulante ou subestipulante). */
  values?: PmeStipulantValues;
  requireAddressFields?: boolean;
  isViewMode?: boolean;
  /** Título do bloco (ex.: "Dados do Estipulante" ou "Subestipulante 1 — …"). */
  title?: string;
};

export const PmeStipulantFormSection = forwardRef<
  PmeStipulantFormSectionHandle,
  Props
>(function PmeStipulantFormSection(
  {
    showContact = false,
    values,
    requireAddressFields = false,
    isViewMode = false,
    title = 'Dados do Estipulante',
  },
  ref,
) {
  const form = useProposalForm({
    defaultValues: values ?? pmeStipulantDefaultValues,
    onSubmit: async () => {},
  });

  useImperativeHandle(
    ref,
    () => ({
      validateRequired: async () => {
        const checks: Array<unknown | Promise<unknown>> = [];
        // CAEPF (pessoa física): Sexo e Data de Nascimento são obrigatórios.
        if (values?.isCaepf) {
          checks.push(
            form.validateField('gender', 'change'),
            form.validateField('dateOfBirth', 'change'),
          );
        }
        if (requireAddressFields) {
          checks.push(
            form.validateField('cep', 'change'),
            form.validateField('street', 'change'),
            form.validateField('number', 'change'),
            form.validateField('neighborhood', 'change'),
            form.validateField('city', 'change'),
            form.validateField('state', 'change'),
            form.validateField('email', 'change'),
            form.validateField('phone', 'change'),
          );
        }
        if (checks.length === 0) return true;
        const results = await Promise.all(checks);
        return !results.some(r => Array.isArray(r) && r.length > 0);
      },
      getValues: () => ({ ...form.state.values }),
    }),
    [form, requireAddressFields, values?.isCaepf],
  );

  const { mutateAsync: fetchZipcode, isPending: isLoadingCep } =
    useFetchZipcode('quotation');

  // Trava por campo, decidida pela ÚLTIMA consulta de CEP
  // (`resolveCepAddressFill`): só um CEP digitado com endereço completo trava.
  // Antes a trava era derivada do valor atual da rua — com CEP de cidade
  // (rua/bairro vazios) a primeira letra digitada travava rua, bairro, cidade
  // e UF. CEP incompleto, CNPJ, endereço gravado e consulta que falhou não
  // travam nada.
  const [lockedFields, setLockedFields] = useState<CepLockedFields>({});
  // CEP da última consulta (ou o hidratado): blur sem alterar o CEP não refaz a
  // busca — refazer apagava a rua/bairro digitados à mão num CEP de cidade.
  const lastCepRef = useRef(onlyDigits(values?.cep));

  // Busca o endereço pelo CEP e preenche os campos (mesmo padrão do fluxo de
  // cotação/proposta: lookup no `onBlur` do campo, não em validators).
  // `onlyEmpty`: completa só o que está vazio (usado após o CNPJ preencher).
  const fetchAndFillCep = async (
    raw: string,
    { onlyEmpty = false, force = false } = {}
  ) => {
    const clean = onlyDigits(raw);
    if (clean.length !== 8) return;
    if (!force && clean === lastCepRef.current) return;
    lastCepRef.current = clean;
    try {
      const result = await fetchZipcode(clean);
      if (!result.success || !result.data || 'error' in result.data) {
        setLockedFields({});
        return;
      }
      const { values: fill, locked } = resolveCepAddressFill(
        result.data,
        form.state.values,
        { onlyEmpty }
      );
      for (const [name, value] of Object.entries(fill)) {
        form.setFieldValue(name as CepAddressField, value);
      }
      setLockedFields(locked);
    } catch (error) {
      // Os campos ficam editáveis; o corretor precisa saber que tem de
      // preenchê-los à mão (são obrigatórios na proposta).
      setLockedFields({});
      toaster.create({
        type: 'error',
        title: 'Não foi possível buscar o CEP',
        description:
          error instanceof Error
            ? `${error.message}. Preencha o endereço manualmente.`
            : 'Preencha o endereço manualmente.',
      });
    }
  };

  const addressResolvedRef = useRef(false);
  useEffect(() => {
    if (addressResolvedRef.current) return;
    // CAEPF também tem 14 caracteres, mas não existe na Receita como CNPJ.
    if (values?.isCaepf) return;
    // CNPJ pode ser alfanumérico (RFB): normaliza preservando letras.
    const cnpjDigits = cleanCnpjOrCpf(form.state.values.cnpj);
    if (cnpjDigits.length !== 14) return;
    addressResolvedRef.current = true;

    const setIfEmpty = (name: keyof PmeStipulantValues, value: string) => {
      if (value && !String(form.state.values[name] || '').trim()) {
        form.setFieldValue(name, value);
      }
    };

    void (async () => {
      const warnCnpjUnavailable = () =>
        toaster.create({
          type: 'warning',
          title: 'Não foi possível consultar o CNPJ',
          description: 'Confira e preencha o endereço manualmente.',
        });
      try {
        const result = await cnpjService.getByCnpj(cnpjDigits);
        if (!result.success || !result.data) {
          warnCnpjUnavailable();
          return;
        }
        const address = result.data.address;
        setIfEmpty('cep', address.zipCode ?? '');
        setIfEmpty('street', address.street ?? '');
        setIfEmpty('number', address.number ?? '');
        setIfEmpty('complement', address.complement ?? '');
        setIfEmpty('neighborhood', address.neighborhood ?? '');
        setIfEmpty('city', address.city ?? '');
        setIfEmpty('state', address.state ?? '');
        // O CNPJ pode devolver CEP sem logradouro — completa via consulta de CEP.
        const cepDigits = onlyDigits(form.state.values.cep);
        if (
          cepDigits.length === 8 &&
          !String(form.state.values.street || '').trim()
        ) {
          await fetchAndFillCep(form.state.values.cep, {
            onlyEmpty: true,
            force: true,
          });
        } else {
          lastCepRef.current = cepDigits;
        }
      } catch {
        // Mantém os campos editáveis e avisa: o endereço é obrigatório.
        warnCnpjUnavailable();
      }
    })();
  }, []);

  const activityOptions = useMemo(() => {
    const current = values?.mainActivity?.trim();
    if (!current || PME_ACTIVITY_OPTIONS.some(o => o.value === current)) {
      return PME_ACTIVITY_OPTIONS;
    }
    return [{ label: current, value: current }, ...PME_ACTIVITY_OPTIONS];
  }, [values?.mainActivity]);

  // CAEPF (pessoa física): rótulo/máscara do documento e campos Sexo/Data.
  const isCaepf = Boolean(values?.isCaepf);
  const todayISODate = DateTime.now().toISODate() || '';
  const minBirthDate = DateTime.now().minus({ years: 99 }).toISODate() || '';

  return (
    <div className='flex flex-col gap-6'>
      {title ? (
        <Text className='font-montserrat text-base font-normal text-alba-black-500'>
          {title}
        </Text>
      ) : null}

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
        <form.AppField
          name='cnpj'
          children={field => (
            <field.TextField
              label={isCaepf ? 'CAEPF' : 'CNPJ'}
              placeholder={isCaepf ? 'XXX.XXX.XXX/XXX-XX' : 'XX.XXX.XXX/XXXX-XX'}
              mask={isCaepf ? 'caepf' : 'cnpj'}
              required
              disabled
            />
          )}
        />
        <form.AppField
          name='companyName'
          children={field => (
            <field.TextField
              label={isCaepf ? 'Nome do Titular' : 'Razão Social'}
              placeholder={isCaepf ? 'Nome do Titular' : 'Razão Social'}
              required
              disabled
            />
          )}
        />
      </div>

      {isCaepf ? (
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
          <form.AppField
            name='gender'
            validators={{
              onChange: ({ value }: { value: string | undefined }) => {
                if (isViewMode) return undefined;
                if (!String(value || '').trim()) return 'Sexo é obrigatório';
                return undefined;
              },
            }}
            children={field => (
              <field.SelectField
                label='Sexo'
                placeholder='Selecione'
                options={PME_GENDER_OPTIONS}
                required
                disabled={isViewMode}
              />
            )}
          />
          <form.AppField
            name='dateOfBirth'
            validators={{
              onChange: ({ value }: { value: string | undefined }) => {
                if (isViewMode) return undefined;
                const raw = String(value || '').trim();
                if (!raw) return 'Data de nascimento é obrigatória';
                const parsed = DateTime.fromISO(raw);
                if (!parsed.isValid) return 'Data de nascimento inválida';
                const age = DateTime.now().diff(parsed, 'years').years;
                if (age < 18 || age > 99)
                  return 'A data de nascimento não é válida!';
                return undefined;
              },
            }}
            children={field => (
              <field.DateField
                label='Data de Nascimento'
                placeholder='dd/mm/aaaa'
                required
                minDate={minBirthDate}
                maxDate={todayISODate}
                disabled={isViewMode}
              />
            )}
          />
        </div>
      ) : null}

      <form.AppField
        name='mainActivity'
        children={field => (
          <field.SelectField
            label='Atividade Principal'
            placeholder='Pesquisar por uma atividade'
            options={activityOptions}
            disabled
          />
        )}
      />

      {showContact && (
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
          <form.AppField
            name='email'
            validators={{
              onChange: ({ value }: { value: string | undefined }) =>
                value?.trim() ? undefined : 'E-mail é obrigatório',
            }}
            children={field => (
              <field.TextField
                label='E-mail'
                lowercase
                placeholder='email@exemplo.com'
                required
                disabled={isViewMode}
              />
            )}
          />
          <form.AppField
            name='phone'
            validators={{
              onChange: ({ value }: { value: string | undefined }) => {
                if (isViewMode) return undefined;
                if (!value?.trim()) return 'Telefone é obrigatório';
                return validatePhone(value) ? undefined : 'Telefone inválido';
              },
            }}
            children={field => (
              <field.TextField
                label='Telefone'
                placeholder='(xx) xxxxx-xxxx'
                mask='(99) 99999-9999'
                required
                disabled={isViewMode}
              />
            )}
          />
        </div>
      )}

      <Text className='font-montserrat text-base font-normal text-alba-black-500'>
        Endereço
      </Text>

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
        <form.AppField
          name='cep'
          validators={cepValidator(requireAddressFields)}
          listeners={{
            // CEP alterado: o que a consulta anterior travou deixa de valer.
            onChange: ({ value }) => {
              if (onlyDigits(value) !== lastCepRef.current) setLockedFields({});
            },
          }}
          children={field => (
            <field.TextField
              label='CEP'
              placeholder='XXXXX-XXX'
              mask='99999-999'
              maxLength={FIELD_MAX_LENGTHS.cep}
              required={requireAddressFields}
              disabled={isViewMode || isLoadingCep}
              onBlur={() => {
                void fetchAndFillCep(form.state.values.cep);
              }}
            />
          )}
        />
      </div>

      <form.AppField
        name='street'
        validators={requiredWhen(requireAddressFields, 'Informe o endereço')}
        children={field => (
          <field.TextField
            label='Endereço'
            placeholder='Rua, Avenida ...'
            required={requireAddressFields}
            disabled={isViewMode || !!lockedFields.street}
          />
        )}
      />

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
        <form.AppField
          name='number'
          validators={
            requireAddressFields
              ? {
                  onChange: ({ value }: { value: unknown }) =>
                    String(value ?? '').trim() ? undefined : 'Informe o número',
                }
              : undefined
          }
          children={field => (
            <field.TextField
              label='Número'
              placeholder='Número'
              required={requireAddressFields}
              maxLength={FIELD_MAX_LENGTHS.addressNumber}
              disabled={isViewMode}
            />
          )}
        />
        <form.AppField
          name='complement'
          children={field => (
            <field.TextField
              label='Complemento'
              placeholder='Apto, Bloco ...'
              maxLength={FIELD_MAX_LENGTHS.complement}
              disabled={isViewMode}
            />
          )}
        />
      </div>

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
        <form.AppField
          name='neighborhood'
          validators={requiredWhen(requireAddressFields, 'Informe o bairro')}
          children={field => (
            <field.TextField
              label='Bairro'
              placeholder='Bairro'
              required={requireAddressFields}
              disabled={isViewMode || !!lockedFields.neighborhood}
            />
          )}
        />
        <form.AppField
          name='city'
          validators={requiredWhen(requireAddressFields, 'Informe a cidade')}
          children={field => (
            <field.TextField
              label='Cidade'
              placeholder='Cidade'
              required={requireAddressFields}
              disabled={isViewMode || !!lockedFields.city}
            />
          )}
        />
        <form.AppField
          name='state'
          validators={requiredWhen(requireAddressFields, 'Informe o estado')}
          children={field => (
            <field.SelectField
              label='Estado'
              placeholder='UF'
              options={PME_UF_OPTIONS}
              required={requireAddressFields}
              disabled={isViewMode || !!lockedFields.state}
            />
          )}
        />
      </div>
    </div>
  );
});
